import { TIMERECORDING_TYPE_OF_WORK_CODES } from '../../../models/complex/time-recording-entry.model';
import { NotificationStateUtilities } from '../../utilities/notification-state-utlities';
import { TimesheetEntryState, TimesheetState } from '../timesheet-collection.state';

/**
 * Milliseconds of an ISO timestamp, treating an unparsable one as the epoch so the comparator
 * never returns `NaN` (which would make the sort order implementation-defined).
 *
 * @param isoTimestamp the timestamp to parse
 * @returns the epoch milliseconds, or 0 when the value does not parse
 */
const epoch = (isoTimestamp: string): number => {
    const parsed = Date.parse(isoTimestamp);
    return Number.isNaN(parsed) ? 0 : parsed;
};

/**
 * Fixed display order of the work-code groups that matter for the same-instant,
 * same-notification tie-break: the travel-out leg before the on-site work before the
 * travel-back leg. TRTO and its standby variant share a rank, as do TRBK and its standby
 * variant — a normal vs. standby leg recorded at the same instant is not mechanically
 * ordered by code, it falls through to {@link compareEntries}'s end-instant tie-break
 * instead. Any other work code has no entry here and is treated as unranked by
 * {@link compareWorkCodes}.
 */
const WORK_CODE_RANK: Readonly<Partial<Record<string, number>>> = {
    [TIMERECORDING_TYPE_OF_WORK_CODES.TRTO]: 0,
    [TIMERECORDING_TYPE_OF_WORK_CODES.TRAVEL_TO_STANDBY]: 0,
    [TIMERECORDING_TYPE_OF_WORK_CODES.OSR]: 1,
    [TIMERECORDING_TYPE_OF_WORK_CODES.TRBK]: 2,
    [TIMERECORDING_TYPE_OF_WORK_CODES.TRAVEL_BACK_STANDBY]: 2,
};

/**
 * Orders two work codes by {@link WORK_CODE_RANK}, or reports them equal when either one is not
 * in that table.
 *
 * Only TRTO/OSR/TRBK have a defined relative order; a code outside that set (e.g. a break or
 * standby code) has no known place among them, so ranking it against a known code would be a
 * guess. Reporting equal here — rather than picking a side — is deliberate: it lets the caller's
 * next tie-break (end instant) decide instead of this comparator inventing an order it has no
 * basis for.
 *
 * @param left first work code
 * @param right second work code
 * @returns negative when `left` ranks first, positive when `right` does, 0 when equal or unranked
 */
const compareWorkCodes = (left: string, right: string): number => {
    const leftRank = WORK_CODE_RANK[left];
    const rightRank = WORK_CODE_RANK[right];
    return leftRank === undefined || rightRank === undefined ? 0 : leftRank - rightRank;
};

/**
 * Chronological order of a timesheet's entries and the whole of what
 * `TimesheetService.sortEntries` / `TimesheetModel.sortTimesheetEntries` did, extended with one
 * more tie-break in between: by start instant; when two entries share a start instant, are both
 * `kind: 'work'`, and belong to the same notification, by their work code's rank group in
 * {@link WORK_CODE_RANK} (travel-out before on-site work before travel-back); and finally by end
 * instant.
 *
 * The work-code tie-break only fires under all three conditions above — different notification,
 * a manual entry on either side, or an unranked code on either side all skip straight to the end
 * instant, exactly as before this tie-break existed. That keeps the common case (no shared start
 * instant) identical to the legacy comparator and confines the new rule to the narrow case it was
 * written for: splitting a travel-out/work/travel-back trio that was recorded as one instant. A
 * normal leg and its standby variant share a rank, so a tie between them (e.g. TRTO vs. its
 * standby counterpart) also falls through to the end-instant tie-break rather than one code
 * mechanically outranking the other.
 *
 * The legacy comparator additionally reported entries with an inverted (negative) duration as
 * mutually equal. That branch is deliberately not reproduced — an entry may be zero-length but
 * never negative, so it guarded a state that must not occur.
 *
 * @param left first entry
 * @param right second entry
 * @returns negative when `left` comes first, positive when `right` does, 0 when they coincide
 */
const compareEntries = (left: TimesheetEntryState, right: TimesheetEntryState): number => {
    const startOrder = epoch(left.start) - epoch(right.start);
    if (startOrder !== 0) {
        return startOrder;
    }

    if (
        left.kind === 'work' &&
        right.kind === 'work' &&
        NotificationStateUtilities.compare(left.notificationId, right.notificationId)
    ) {
        const workCodeOrder = compareWorkCodes(left.workCode, right.workCode);
        if (workCodeOrder !== 0) {
            return workCodeOrder;
        }
    }

    return epoch(left.end) - epoch(right.end);
};

/**
 * Restores the timesheet's sorted-entries invariant. Every reducer case that writes `entries`
 * goes through this, so every reader can rely on the order without sorting again.
 *
 * @param timesheet the timesheet whose entries may be out of order
 * @returns the timesheet with its entries in chronological order
 */
export const withSortedEntries = (timesheet: TimesheetState): TimesheetState => ({
    ...timesheet,
    // `?? []` guards the one case the type cannot: a developer's IndexedDB left over from an
    // azure-ngrx run that predates the flat shape has no `entries` at all, and hydrating it
    // would otherwise throw before the slice is ever usable.
    entries: (timesheet.entries ?? []).toSorted(compareEntries),
});
